import {
  BaseBotData,
  BotType,
  LanguageNameSpaces,
  OrderTypes,
} from "@shared/types";
import React, { ReactNode } from "react";
import { useSharedBotStrategySummary } from "@shared/features/bots/hooks";
import CustomStrategyDisplay from "features/bots/BotDrawer/CustomStrategyDisplay";
import BotOverrideDisplay from "features/bots/BotDrawer/BotOverrideDisplay";
import { USING_CHANNEL_SETTING } from "@shared/features/bots/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useBotStrategySummaryProps {
  botData: BaseBotData;
  orderType: OrderTypes;
  isAdvanced?: boolean;
  pricePrecision?: number;
  isChannelConfiguration?: boolean;
  isAdminConfiguration?: boolean;
}

const useBotStrategySummary = ({
  botData,
  orderType,
  isAdvanced,
  pricePrecision,
  isChannelConfiguration,
  isAdminConfiguration,
}: useBotStrategySummaryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const strategyData = useSharedBotStrategySummary(
    botData,
    orderType,
    isAdvanced,
    pricePrecision,
    isChannelConfiguration,
    isAdminConfiguration
  );
  if (!strategyData) return null;

  const isSignalBots =
    isAdminConfiguration || botData.bot_type === BotType.SIGNAL;

  if (isSignalBots) {
    return strategyData.signalsBotStrategyData?.map((displayData) => {
      let value: ReactNode;
      if (displayData.targetsDisplay)
        value = (
          <CustomStrategyDisplay
            targetsDisplayData={strategyData.targetsDisplay}
          />
        );
      else if (typeof displayData.value === "string") value = displayData.value;
      else if (Array.isArray(displayData.value)) {
        value = displayData.value.map((text, index) => (
          <div key={index}>{text}</div>
        ));
      }
      return {
        title: displayData.title,
        value: (
          <>
            {value}
            {displayData.overrideType && !isChannelConfiguration && (
              <BotOverrideDisplay
                botData={botData}
                overrideType={displayData.overrideType}
                isChannelValue={displayData.value === t(USING_CHANNEL_SETTING)}
              />
            )}
          </>
        ),
      };
    });
  }

  return [
    {
      title: strategyData.title,
      value: strategyData.displayString ?? (
        <>
          {strategyData.gridsData?.map((text, index) => (
            <div key={index}>{text}</div>
          ))}
          {strategyData.strategyData?.map((text, index) => (
            <div key={index}>{text}</div>
          ))}
          <CustomStrategyDisplay
            targetsDisplayData={strategyData.targetsDisplay}
          />
        </>
      ),
    },
  ];
};

export default useBotStrategySummary;
